import { useMemo, useState, useCallback } from 'react';
import { createEditor, Element as SlateElement, Descendant, Editor } from 'slate';
import { Slate, Editable, withReact, ReactEditor } from 'slate-react';
import { withHistory } from 'slate-history';
import { useToast } from '@/hooks/use-toast';
import { ElementRenderer, LeafRenderer, CustomElement } from './utils/element-renderer';
import { SMSEditorToolbar } from './toolbar/sms-editor-toolbar';
import { withImages } from './plugins/with-images';
import { withStopMessage } from './plugins/with-stop-message';
import { withVariables } from './plugins/with-variables';
import { withShortLinks } from './plugins/with-shortlinks';
import { withDocumentStructure } from './plugins/with-document-structure';
import { withContactCards, ContactCardData } from './plugins/with-contact-cards';
import { SlashCommandList } from './slash-commands/slash-command-list';
import { createSlashCommands } from './slash-commands/slash-commands-config';
import { ImageUploadDialog } from './toolbar/image-upload-dialog';
import { StopMessageDialog } from './toolbar/stop-message-dialog';
import { EditorMetrics } from './utils/editor-metrics';
import { useImageHandling } from './hooks/use-image-handling';
import { useEditorContent } from './hooks/use-editor-content';
import { useSlashCommandHandling } from './hooks/use-slash-command-handling';
import { useSlashCommandActions } from './hooks/use-slash-command-actions';
import { useEmojiPicker } from './hooks/use-emoji-picker';
import { useStopMessage } from './hooks/use-stop-message';
import { createToolbarActions } from './toolbar/toolbar-config';
import { cn } from '@/lib/utils';
import { useVariableHandling } from './hooks/use-variable-handling';
import { VariableDialog } from './toolbar/variable-dialog';
import { ShortLinkDialog } from './toolbar/shortlink-dialog';
import { useShortLink } from './hooks/use-shortlink';
import { ShortLinkElement } from './plugins/with-shortlinks';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ContactCardDialog } from './contact-card/contact-card-dialog';
import { useContactCard } from './hooks/use-contact-card';
import { BASE_INITIAL_VALUE } from './types';
import { useEditorSync } from './hooks/use-editor-sync';

interface SMSEditorProps {
  initialValue?: Descendant[];
  onChange?: (editorState: Descendant[]) => void;
}

/**
 * Renders a placeholder when the editor's first paragraph is empty.
 * Positioned absolutely over the editor content.
 *
 * Note: We consider a paragraph empty if it only contains newlines or whitespace.
 * This allows for visual spacing while still showing the placeholder.
 */
function EditorPlaceholder({ value }: { value: Descendant[] }) {
  const firstNode = value[0] as CustomElement | undefined;
  const text = firstNode?.children[0]?.text ?? '';
  const shouldShow = firstNode?.type === 'paragraph' && text.trim() === '';

  if (!shouldShow) {
    return null;
  }

  return (
    <div className="absolute top-3 left-3 text-muted-foreground select-none pointer-events-none">
      Type / for commands...
    </div>
  );
}

/**
 * A SMS/MMS message composer built on Slate.js.
 *
 * Key Features:
 * - Rich text editing with personalization (variables and shortlinks)
 * - MMS support with image attachments and previews
 * - Compliance: Enforces carrier rules and required STOP message
 * - Smart tools: Emoji picker, slash commands for quick actions
 *
 * Important Implementation Notes:
 * - Plugin order matters! Document structure must be first, followed by content plugins
 * - Contact Cards and Images are limited to one per message (document structure plugin)
 * - STOP message is always preserved at the bottom
 * - Character count and segment tracking are estimates but are not guaranteed
 *
 * @see README.md for detailed architecture overview
 */
export function SMSEditor({ initialValue, onChange }: SMSEditorProps) {
  const { toast } = useToast();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [editorValue, setEditorValue] = useState<Descendant[]>(initialValue ?? BASE_INITIAL_VALUE);

  const editor = useMemo(() => {
    return withDocumentStructure(
      withContactCards(
        withVariables(
          withStopMessage(
            withImages(withShortLinks(withHistory(withReact(createEditor()))), setErrorMessage)
          )
        )
      )
    );
  }, [setErrorMessage]);

  useEditorSync({ editor, initialValue });

  const { showStopDialog, setShowStopDialog, currentStopMessage, handleStopMessageChange } =
    useStopMessage(editor);
  const {
    showVariableDialog,
    setShowVariableDialog,
    selectedVariable,
    handleVariableSelect,
    handleVariableEdit
  } = useVariableHandling(editor);

  const {
    hasImage,
    showImageDialog,
    setShowImageDialog,
    handleFileSelected,
    handleDeleteImage,
    preventTypingAfterImage,
    isDragging,
    dragAndDropProps
  } = useImageHandling(editor, editorValue, setErrorMessage);

  const {
    metrics,
    handleChange: handleMetricsChange,
    insertEmoji
  } = useEditorContent({
    editor,
    onChange: (newValue) => {
      setEditorValue(newValue);
      onChange?.(newValue);
    }
  });

  const {
    isOpen: isCommandOpen,
    commandText,
    targetRange,
    handleKeyDown,
    handleCommand,
    decorate,
    closeMenu
  } = useSlashCommandHandling(editor);

  const { isOpen: isEmojiOpen, openPicker, togglePicker } = useEmojiPicker();

  const shortLinkForm = useShortLink(editor);
  const [showShortLinkDialog, setShowShortLinkDialog] = useState(false);
  const [_, setEditingShortLink] = useState<ShortLinkElement | null>(null);

  const handleNewShortLink = useCallback(() => {
    shortLinkForm.captureSelection();
    setEditingShortLink(null);
    setShowShortLinkDialog(true);
  }, [shortLinkForm]);

  const handleEditShortLink = useCallback(
    (element: ShortLinkElement) => {
      const path = ReactEditor.findPath(editor, element);
      shortLinkForm.captureSelection();
      setEditingShortLink(element);
      setShowShortLinkDialog(true);
      shortLinkForm.loadShortLink(element, path);
    },
    [editor, shortLinkForm]
  );

  const {
    handleEmojiCommand,
    handleImageCommand,
    handleVariableCommand,
    handleShortLinkCommand,
    handleContactCommand
  } = useSlashCommandActions({
    onImageClick: () => {
      setShowImageDialog(true);
    },
    onEmojiSelect: openPicker,
    onVariableClick: () => {
      setShowVariableDialog(true);
    },
    onShortLinkClick: handleNewShortLink,
    handleCommand,
    onContactClick: () => {
      setIsContactOpen(true);
    }
  });

  const slashCommands = useMemo(
    () =>
      createSlashCommands({
        onImageClick: handleImageCommand,
        onEmojiClick: handleEmojiCommand,
        onVariableClick: handleVariableCommand,
        onShortLinkClick: handleShortLinkCommand,
        onContactClick: handleContactCommand,
        editor,
        toast
      }),
    [
      handleImageCommand,
      handleEmojiCommand,
      handleVariableCommand,
      handleShortLinkCommand,
      editor,
      toast,
      handleContactCommand
    ]
  );

  const [isContactOpen, setIsContactOpen] = useState(false);
  const toggleContact = useCallback(() => {
    setIsContactOpen(!isContactOpen);
  }, [isContactOpen]);

  const {
    showContactCardDialog,
    setShowContactCardDialog,
    selectedContactCard,
    setSelectedContactCard,
    getContactCards,
    handleContactCardCreate,
    handleContactCardEdit,
    handleContactCardDelete,
    handleContactCardInsert
  } = useContactCard(editor);

  const toolbarActions = useMemo(
    () =>
      createToolbarActions({
        onImageClick: () => {
          setShowImageDialog(true);
        },
        onEmojiSelect: insertEmoji,
        onAlertClick: () => {
          setShowStopDialog(true);
        },
        onVariableClick: () => {
          setShowVariableDialog(true);
        },
        onShortLinkClick: handleNewShortLink,
        hasImage,
        isEmojiOpen,
        togglePicker,
        isContactOpen,
        toggleContact,
        handleContactCardInsert,
        getContactCards,
        setShowContactCardDialog,
        setSelectedContactCard
      }),
    [
      setShowImageDialog,
      insertEmoji,
      setShowStopDialog,
      setShowVariableDialog,
      handleNewShortLink,
      hasImage,
      isEmojiOpen,
      togglePicker,
      isContactOpen,
      toggleContact,
      handleContactCardInsert,
      getContactCards,
      setShowContactCardDialog,
      setSelectedContactCard
    ]
  );

  const hasVariables = useMemo(() => {
    const [match] = Editor.nodes(editor, {
      at: [],
      match: (n) => SlateElement.isElement(n) && n.type === 'variable'
    });
    return !!match;
  }, [editor]);

  return (
    <TooltipProvider>
      <div className="flex flex-col w-full">
        <div
          className={cn(
            'rounded-lg w-full',
            errorMessage ? 'border-2 border-destructive-foreground' : 'border'
          )}
        >
          <div
            className={cn(
              'relative min-h-[240px]',
              'max-h-[480px] overflow-hidden',
              'transition-all duration-200',
              isDragging && 'ring-2 ring-primary/20 bg-primary/5'
            )}
            {...dragAndDropProps}
          >
            <EditorPlaceholder value={editorValue} />
            <Slate editor={editor} initialValue={editorValue} onChange={handleMetricsChange}>
              {isCommandOpen && (
                <SlashCommandList
                  editor={editor}
                  commandText={commandText}
                  targetRange={targetRange}
                  onSelect={handleCommand}
                  commands={slashCommands}
                  closeMenu={closeMenu}
                />
              )}
              <div className="max-h-[480px] overflow-y-auto">
                <Editable
                  className={cn('p-3 w-full focus:outline-none font-inter', 'min-h-[240px]')}
                  renderElement={(props) => (
                    <ElementRenderer
                      {...props}
                      editor={editor}
                      onDeleteImage={handleDeleteImage}
                      onEditStopMessage={() => {
                        setShowStopDialog(true);
                      }}
                      onEditVariable={handleVariableEdit}
                      onEditShortLink={handleEditShortLink}
                      onEditContactCard={(contactCardElement) => {
                        setSelectedContactCard(contactCardElement);
                        setShowContactCardDialog(true);
                      }}
                      onDeleteContactCard={(id: string) => {
                        handleContactCardDelete(id);
                      }}
                    />
                  )}
                  renderLeaf={(props) => <LeafRenderer {...props} />}
                  onKeyDown={(e) => {
                    if (preventTypingAfterImage(e)) {
                      return;
                    }
                    handleKeyDown(e);
                  }}
                  decorate={decorate}
                />
              </div>
            </Slate>
            <EditorMetrics metrics={metrics} value={editorValue} hasVariables={hasVariables} />
          </div>

          <SMSEditorToolbar actionGroups={toolbarActions} />
        </div>
        {errorMessage && (
          <div className="mt-2 font-dm-sans text-xs text-destructive-foreground font-normal leading-normal">
            {errorMessage}
          </div>
        )}
      </div>

      <ImageUploadDialog
        open={showImageDialog}
        onOpenChange={setShowImageDialog}
        onFileSelected={handleFileSelected}
        onImageUploaded={(result) => {
          // Now we have both the file and the Vercel URL
          handleFileSelected(result.file, result.url);
        }}
      />

      <StopMessageDialog
        open={showStopDialog}
        onOpenChange={setShowStopDialog}
        selectedMessage={currentStopMessage}
        onMessageSelected={handleStopMessageChange}
      />

      <VariableDialog
        open={showVariableDialog}
        onOpenChange={setShowVariableDialog}
        selectedVariable={selectedVariable}
        onVariableSelected={handleVariableSelect}
      />

      <ShortLinkDialog
        open={showShortLinkDialog}
        onOpenChange={(open) => {
          if (!open) {
            setEditingShortLink(null);
            shortLinkForm.resetState();
          }
          setShowShortLinkDialog(open);
        }}
        editor={editor}
        shortLinkForm={shortLinkForm}
      />

      <ContactCardDialog
        open={showContactCardDialog}
        onOpenChange={setShowContactCardDialog}
        initialData={selectedContactCard?.data}
        onSubmit={(data: Omit<ContactCardData, 'id' | 'vcfString'>) => {
          if (selectedContactCard) {
            handleContactCardEdit({ ...selectedContactCard.data, ...data });
          } else {
            handleContactCardCreate(data);
          }
          setShowContactCardDialog(false);
        }}
      />
    </TooltipProvider>
  );
}
